/* eslint-disable */
import {
  Button,
  message,
  Modal,
  Space,
  Spin
} from 'antd';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'react-query';
import { useSearchParams } from 'react-router-dom';

import InfoCard from './components/InfoCard';
import TimekeepingDetail from './components/Timekeeping';

import { LoadingOutlined, PrinterOutlined } from '@ant-design/icons';
import { useAppSelector } from 'app/store';
import CategoryCalculate from 'common/components/CategoryCalculate';
import HeaderPage from 'common/components/HeaderPage';
import { exportSalaryRecordService, getExportByCodeService } from 'common/services/extends/exports';
import {
  getPayrollPeriodsByIdService,
  supApproveSalaryByIdService
} from 'common/services/extends/payrollPeriods';
import { getPermissionFromProjectByIdService } from 'common/services/extends/project';
import roles, { getPermission } from 'configs/roles';
import SalaryCalculating from './components/SalaryCalculating';
import { getLocalStorage, setLocalStorage } from 'common/services/common/storage';
import LOCAL_STORAGE from 'common/utils/constant';
import { ExportAttendanceRecordRequestTypes } from 'common/services/extends/exports/types';
import PopupGuideSettingPopups from 'common/components/PopupGuideSettingPopups';
import { isPopupBlocked } from 'common/utils/functions';

const SalaryTableDetail: React.FC<ActiveRoles> = () => {
  /* Hooks */
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();

  const rolesUser = useAppSelector((state) => state.auth.roles);

  const projectId = searchParams.get('projectId') || '';
  const payrollPeriodId = searchParams.get('payrollPeriodId') || '';
  const staffFullTimeId = searchParams.get('staffFullTimeId') || '';

  /* States */
  const [downloading, setDownloading] = React.useState(false);
  const [isPopupBlock, setIsPopupBlock] = React.useState({
    status: false,
    link: ''
  });
  const [checkDownload, setCheckDownload] = React.useState({
    status: false,
    code: ''
  });

  /* ref */
  const clickedDownloadRef = React.useRef<boolean>(false);
  const checkStatusExportRef = React.useRef<boolean>(false);

  const { data: payrollData, isLoading: isLoadingPayroll, refetch } = useQuery(
    ['payroll-periods-by-id', payrollPeriodId],
    () => getPayrollPeriodsByIdService(payrollPeriodId),
    {
      enabled: !!payrollPeriodId,
    }
  );

  const { mutate: supApproveSalaryMutate, isLoading: supApproveSalaryLoading } = useMutation(
    ['supApproveSalary'],
    async () => supApproveSalaryByIdService(payrollPeriodId),
    {
      onSuccess: () => {
        refetch();
        message.success(t('message.approveSuccess'));
      },
      onError: () => {
        message.error(t('message.approveError'));
      }
    }
  );

  const handleApprove = async () => {
    if (getPermission(rolesUser, roles.SUP_APPROVE_SALARY)) {
      supApproveSalaryMutate();
    }
  };

  const { isLoading: isLoadingPermission, data: permission } = useQuery(
    ['project-permission-detail', projectId, staffFullTimeId],
    () => getPermissionFromProjectByIdService(
      projectId,
      {
        staffFullTimeId
      }
    ),
    {
      enabled: !!staffFullTimeId && !!projectId,
    }
  );

  const {
    mutate: exportSalaryRecordMutate,
  } = useMutation(
    ['exportSalaryRecordMutate'],
    async (params: ExportAttendanceRecordRequestTypes) => exportSalaryRecordService({
      payrollPeriodId: params.payrollPeriodId
    }),
    {
      onSuccess: (data) => {
        if (data?.downloadUrl) {
          const blocked = getLocalStorage(LOCAL_STORAGE.BLOCKED_POPUPS);
          if (blocked) {
            setIsPopupBlock({
              status: true,
              link: data.downloadUrl
            });
            return;
          }
          message.success(t('message.exportSuccess'));
          setCheckDownload({
            status: false,
            code: ''
          });
          window.open(data.downloadUrl, '_blank');
        } else {
          setCheckDownload({
            status: true,
            code: data.code || ''
          });
        }
      },
      onError: () => {
        message.error(t('message.exportError'));
      }
    }
  );

  const onHandleDownloadSalary = async () => {
    try {
      if (clickedDownloadRef.current) {
        return;
      }
      clickedDownloadRef.current = true;

      setDownloading(true);
      setCheckDownload({
        status: false,
        code: ''
      });

      exportSalaryRecordMutate({
        payrollPeriodId: Number(payrollPeriodId)
      });
    } catch (error) {
      message.error(t('message.exportError'));
      setDownloading(false);
      clickedDownloadRef.current = false;
      setCheckDownload({
        status: false,
        code: ''
      });
      setIsPopupBlock({
        status: false,
        link: ''
      });
    }
  }

  useEffect(() => {
    const blocked = isPopupBlocked('');
    if (blocked) {
      setLocalStorage(LOCAL_STORAGE.BLOCKED_POPUPS, 'true');
    } else {
      window.localStorage.removeItem(LOCAL_STORAGE.BLOCKED_POPUPS);
    }
  }, []);

  React.useEffect(() => {
    if (checkDownload.status && checkDownload.code) {
      let numberOfChecks = 0;
      const interval = setInterval(async () => {
        if (checkStatusExportRef.current) {
          console.log('Already checking status, skipping this interval.');
          return;
        }

        // Limit the number of checks to prevent infinite loops
        if (numberOfChecks >= 9) {
          clearInterval(interval);
          message.error(t('message.exportError'));
          setCheckDownload({
            status: false,
            code: ''
          });
          setDownloading(false);
          clickedDownloadRef.current = false;
          return;
        }
        console.log('Checking download status...');
        try {
          checkStatusExportRef.current = true;
          numberOfChecks += 1;
          const response = await getExportByCodeService(checkDownload.code);
          if (response?.status === 'failed') {
            clearInterval(interval);
            message.error(t('message.exportError'));
            setCheckDownload({
              status: false,
              code: ''
            });
            setIsPopupBlock({
              status: false,
              link: ''
            });
            setDownloading(false);
            clickedDownloadRef.current = false;
            return;
          }
          if (response?.downloadUrl && response.status === 'success') {
            const blocked = getLocalStorage(LOCAL_STORAGE.BLOCKED_POPUPS);
            if (blocked && response) {
              setIsPopupBlock({
                status: true,
                link: response?.downloadUrl
              });
              clearInterval(interval);
              setCheckDownload({
                status: false,
                code: ''
              });
              setDownloading(false);
              clickedDownloadRef.current = false;
              return;
            }
            clearInterval(interval);
            window.open(response.downloadUrl, '_blank');
            setCheckDownload({
              status: false,
              code: ''
            });
            setDownloading(false);
            clickedDownloadRef.current = false;
          } else {
            console.log('Download not ready yet, checking again...');
          }
        } catch (error) {
          clearInterval(interval);
          setIsPopupBlock({
            status: false,
            link: ''
          });
          console.error('Error checking download status:', error);
          message.error(t('message.exportError'));
          setCheckDownload({
            status: false,
            code: ''
          });
          setDownloading(false);
          clickedDownloadRef.current = false;
        } finally {
          checkStatusExportRef.current = false;
        }
      }, 3000); // Check every 3 seconds

      return () => clearInterval(interval); // Clear interval on unmount
    }
  }, [checkDownload, t]);

  /* Render */
  return (
    <>
      <HeaderPage
        fixed
        title={t('salary.tableSalaryDetail')}
        rightHeader={(
          <Space size={16}>
            {getPermission(rolesUser, roles.SALARY_RECORD) && (
              <Button
                type="primary"
                icon={<PrinterOutlined />}
                onClick={onHandleDownloadSalary}
              >
                {
                  downloading ? (
                    <Space>
                      {t('exportReports.checkingDownload')}
                      <LoadingOutlined style={{ fontSize: 16 }} />
                    </Space>
                  ) : t('exportReports.downloadSalaryReport')
                }
              </Button>
            )}
            <Button
              type="primary"
              disabled={
                supApproveSalaryLoading
                || payrollData?.payrollPeriodData?.supApproved
                || !getPermission(rolesUser, roles.SUP_APPROVE_SALARY)
                || isLoadingPermission
                || !permission?.canDo
              }
              onClick={() => {
                Modal.confirm({
                  className: 't-pagetable_deleteRecordModal',
                  autoFocusButton: 'cancel',
                  okText: t('system.ok'),
                  cancelText: t('system.cancel'),
                  cancelButtonProps: {
                    type: 'primary',
                  },
                  okButtonProps: {
                    type: 'default',
                  },
                  title: t('message.confirmApproveTableSalary'),
                  onOk: () => handleApprove(),
                });
              }}
            >
              {
                payrollData?.payrollPeriodData?.supApproved ? t('salary.sendedApprove') : t('salary.sendApprove')
              }
            </Button>
          </Space>
        )}
      />
      <Spin spinning={isLoadingPayroll}>
        <div className="t-mainlayout_wrapper">
          <Space direction="vertical" size={12} style={{ width: '100%' }}>
            <InfoCard />
            <TimekeepingDetail
              endDate={payrollData?.payrollPeriodData?.endDate || ''}
              fromDate={payrollData?.payrollPeriodData?.startDate || ''}
              name={payrollData?.payrollPeriodData?.name || ''}
            />
            <SalaryCalculating approved={payrollData?.payrollPeriodData?.supApproved} disableAction={!permission?.canDo} />
            <CategoryCalculate view={payrollData?.payrollPeriodData?.supApproved} disableAction={!permission?.canDo} />
          </Space>
        </div>
      </Spin>

      <PopupGuideSettingPopups
        isLoading={downloading}
        isPopupBlock={isPopupBlock}
        onClose={() => setIsPopupBlock({ status: false, link: '' })}
      />
    </>
  );
};

export default SalaryTableDetail;
